import Table from 'antd/lib/table';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import React, { useState } from 'react';
import type { ResizeCallbackData } from 'react-resizable';
import { Resizable } from 'react-resizable';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import ReactDragListView from 'react-drag-listview';

 interface DataType {
   key: React.Key;
   date: string;
   amount: number;
   type: string;
   note: string;
 }

 interface IResizableAndDraggableTableProps {
   className: string;
   id: string;
   rowKey?: string; 
   columns: any;
   dataSource: any;
   pagination: false | TablePaginationConfig;
   rowSelection: any;
   components: any;
   onRow: any;
   handleSortClick?: (sortValue: string) => void;
   changeTableHandler?: any;
   updateState?: (from: number, to: number) => void;
 }

 export const ResizableTitle = (
   props: React.HTMLAttributes<any> & {
     onResize: (e: React.SyntheticEvent<Element>, data: ResizeCallbackData) => void;
     width: number;
     handleSortClick?: (sortValue: string) => void;
     sortValue?: string;
   }
 ) => {
   const { onResize, width, handleSortClick, sortValue, ...restProps } = props;

   if (!width) {
     return <th {...restProps} />;
   }

   return (
     <Resizable
       width={width}
       height={0}
       handle={<span className="react-resizable-handle" />}
       onResize={onResize}
       draggableOpts={{ enableUserSelectHack: false }}
     >
       <th className={'ant-table-cell'}>
         <div className={'dragHandler'}>
           <div className={'sortHandler'} onClick={(e) => sortValue && handleSortClick && handleSortClick(sortValue)}>
             <span {...restProps} />
           </div>
         </div>
       </th>
     </Resizable>
   );
 };

 const ResizableAndDraggableTable: React.FC<IResizableAndDraggableTableProps> = (props) => {
   const { columns: cols, className, dataSource, onRow, rowSelection, pagination, id, handleSortClick, components, changeTableHandler, updateState } = props;
   const [columns, setColumns] = useState<ColumnsType<DataType>>(cols);

   const handleResize =
     (index: number) =>
     (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
       const newColumns = [...columns];
       newColumns[index] = {
         ...newColumns[index],
         width: size.width,
       };
       setColumns(newColumns);
     };

   const mergeColumns: any = columns.map((col, index) => ({
     ...col,
     onHeaderCell: (column: any) => ({
       width: (column as ColumnType<DataType>).width,
       onResize: handleResize(index),
       handleSortClick: handleSortClick,
       sortValue: column.sortValue,
     }),
   }));

   const onDragEnd = (fromIndex: any, toIndex: any) => {
     if (updateState) {
       updateState?.(fromIndex, toIndex);
     } else {
       if (rowSelection && (fromIndex == 0 || toIndex == 0)) {
         return;
       }
       const newColumns = [...columns];
       const item = newColumns.splice(rowSelection ? fromIndex - 1 : fromIndex, 1)[0];
       newColumns.splice(rowSelection ? toIndex - 1 : toIndex, 0, item);
       setColumns(newColumns);
     }
   };

   return (
     <ReactDragListView onDragEnd={onDragEnd} handleSelector=".dragHandler" nodeSelector="th">
       <Table
         className={className}
         id={id}
         columns={mergeColumns}
         dataSource={dataSource}
         pagination={pagination}
         rowSelection={rowSelection}
         onRow={onRow}
         components={components}
         onChange={changeTableHandler}
         scroll={{ x: '100%' }}
         bordered
       />
     </ReactDragListView>
   );
 };

export default ResizableAndDraggableTable;
